// 📁 RechercheSalaire.js
import React, { useEffect, useState, useContext } from "react";
import axios from "../../context/AxiosInstance";
import { Table, Button, Form, Row, Col, Spinner, Badge, Nav, Tab, Alert } from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import SalaireEditModal from "./SalaireEditModal";
import ConfirmDeleteSalaireModal from "./ConfirmDeleteSalaireModal";
import VerrouillageSalaireModal from "./VerrouillageSalaireModal";
import SalaireDuplicationModal from "./SalaireDuplicationModal";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Select from "react-select";
import {
  FaSearch,
  FaSyncAlt,
  FaPrint,
  FaCopy,
  FaLock,
  FaEdit,
  FaTrash,
  FaUser,
  FaCalendarAlt,
  FaCalendarDay,
  FaChartBar,
} from "react-icons/fa";
import "../../styles/historique-periodes.css";
import "./RechercheSalaire.css";
import { getInitiales, RsKpiCard } from "./SalairesUi";

const ORDRE_MOIS = {
  Janvier: 1, Février: 2, Mars: 3, Avril: 4, Mai: 5, Juin: 6,
  Juillet: 7, Août: 8, Septembre: 9, Octobre: 10, Novembre: 11, Décembre: 12,
};

const trierPeriodesChronologique = (periodes = []) =>
  [...periodes].sort((a, b) => {
    if (a.annee !== b.annee) return a.annee - b.annee;
    return (ORDRE_MOIS[a.mois] || 13) - (ORDRE_MOIS[b.mois] || 13);
  });

const grouperPeriodesParAnnee = (periodes) => {
  const sorted = trierPeriodesChronologique(periodes);
  return sorted.reduce((acc, p) => {
    const annee = String(p.annee);
    if (!acc[annee]) acc[annee] = [];
    acc[annee].push(p);
    return acc;
  }, {});
};

const RechercheSalaire = () => {
  const { token, role } = useContext(AuthContext);
  const [salaires, setSalaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [calculsData, setCalculsData] = useState(null);
  const [calculsMensuelsData, setCalculsMensuelsData] = useState(null);
  const [loadingCalculs, setLoadingCalculs] = useState(false);
  const [loadingCalculsMensuels, setLoadingCalculsMensuels] = useState(false);
  const [verrouillageInfo, setVerrouillageInfo] = useState(null);

  const [contrats, setContrats] = useState([]);
  const [crgs, setCrgs] = useState([]);

  const [filters, setFilters] = useState({
    id_contrat: "",
    annee: "",
    mois: "",
  });
  /** Critères de la dernière recherche validée (bouton Rechercher ou clic historique) */
  const [appliedFilters, setAppliedFilters] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSalaire, setSelectedSalaire] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showVerrouillageModal, setShowVerrouillageModal] = useState(false);
  const [employeInfo, setEmployeInfo] = useState(null);
  const [updatingBulletin, setUpdatingBulletin] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [historiquePeriodes, setHistoriquePeriodes] = useState(null);
  const [loadingHistorique, setLoadingHistorique] = useState(false);

  // Duplication avancée
  const [showDupModal, setShowDupModal] = useState(false);
  const [dupMonth, setDupMonth] = useState("");
  const [dupMonths, setDupMonths] = useState([]);
  const [dupYear, setDupYear] = useState("");
  const [dupMultiMonths, setDupMultiMonths] = useState(false);
  // Panier de périodes cibles multi-années : [{ annee, mois }]
  const [dupTargets, setDupTargets] = useState([]);
  const [enableDupTargets, setEnableDupTargets] = useState(false);
  const [selectedTargetEmployeeIds, setSelectedTargetEmployeeIds] = useState([]);

  const moisOptions = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];
  const annees = [2024, 2025, 2026];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resContrats, resCrgs] = await Promise.all([
          axios.get("/contrats/", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get("/crg/", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setContrats(resContrats.data.data || []);
        setCrgs(resCrgs.data.data || []);
      } catch (err) {
        toast.error("Erreur chargement contrats/CRG");
      }
    };

    if (token) fetchData();
  }, [token]);

  const fetchSalaires = async (params) => {
    setLoading(true);
    try {
      const res = await axios.get("/salaires/", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          id_contrat: params.id_contrat,
          annee: params.annee,
          mois: params.mois,
        },
      });
      setSalaires(res.data.data || []);
      setHasSearched(true);

      try {
        const verrouillageRes = await axios.get("/salaires/verifier-verrouillage/", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            id_contrat: params.id_contrat,
            annee: params.annee,
            mois: params.mois,
          },
        });
        setVerrouillageInfo(verrouillageRes.data.data);
      } catch (err) {
        setVerrouillageInfo(null);
      }
    } catch (err) {
      toast.error("Erreur chargement salaires");
      setSalaires([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalculsSalaire = async (params) => {
    if (!params.id_contrat || !params.annee) return;
    setLoadingCalculs(true);
    try {
      const res = await axios.get("/salaires/calculs-salaire/", {
        headers: { Authorization: `Bearer ${token}` },
        params: { id_contrat: params.id_contrat, annee: params.annee },
      });
      setCalculsData(res.data.data);
    } catch (err) {
      toast.error("Erreur chargement des calculs annuels");
      setCalculsData(null);
    } finally {
      setLoadingCalculs(false);
    }
  };

  const fetchHistoriquePeriodes = async (params) => {
    if (!params.id_contrat) return;
    setLoadingHistorique(true);
    try {
      const res = await axios.get("/salaires/historique-periodes/", {
        headers: { Authorization: `Bearer ${token}` },
        params: { id_contrat: params.id_contrat },
      });
      setHistoriquePeriodes(res.data.data || null);
    } catch (err) {
      setHistoriquePeriodes(null);
    } finally {
      setLoadingHistorique(false);
    }
  };

  const fetchCalculsMensuels = async (params) => {
    if (!params.id_contrat || !params.annee || !params.mois) return;
    setLoadingCalculsMensuels(true);
    try {
      const res = await axios.get("/salaires/calculs-mensuels/", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          id_contrat: params.id_contrat,
          annee: params.annee,
          mois: params.mois,
        },
      });
      setCalculsMensuelsData(res.data.data);
    } catch (err) {
      toast.error("Erreur chargement des calculs mensuels");
      setCalculsMensuelsData(null);
    } finally {
      setLoadingCalculsMensuels(false);
    }
  };

  const runSearch = async (searchFilters) => {
    const params = searchFilters || filters;
    if (!params.id_contrat || !params.annee || !params.mois) {
      toast.warning("Veuillez sélectionner un employé, une année et un mois pour effectuer la recherche");
      return;
    }
    const normalized = {
      id_contrat: params.id_contrat,
      annee: String(params.annee),
      mois: params.mois,
    };
    setAppliedFilters(normalized);
    await fetchSalaires(normalized);
    await fetchHistoriquePeriodes(normalized);
    await fetchCalculsSalaire(normalized);
    await fetchCalculsMensuels(normalized);
  };

  const handleSearch = () => runSearch(filters);

  const handleHistoriquePeriodeClick = (periode) => {
    const newFilters = {
      ...filters,
      id_contrat: filters.id_contrat || appliedFilters?.id_contrat,
      mois: periode.mois,
      annee: String(periode.annee),
    };
    setFilters(newFilters);
    runSearch(newFilters);
  };

  const handleReset = () => {
    setFilters({ id_contrat: "", annee: "", mois: "" });
    setAppliedFilters(null);
    setSalaires([]);
    setHasSearched(false);
    setCalculsData(null);
    setCalculsMensuelsData(null);
    setVerrouillageInfo(null);
    setHistoriquePeriodes(null);
  };

  const getActiveSearchParams = () => appliedFilters || filters;

  const refreshCurrentSearch = () => {
    if (appliedFilters) return runSearch(appliedFilters);
  };

  const openEdit = (salaire) => { setSelectedSalaire(salaire); setShowEditModal(true); };
  const openDelete = (salaire) => { setSelectedSalaire(salaire); setShowDeleteModal(true); };

  const handleDeleteAllSalaires = async () => {
    const params = getActiveSearchParams();
    if (!params.id_contrat || !params.annee || !params.mois) {
      toast.error("Veuillez sélectionner un employé, une année et un mois");
      return;
    }
    if (verrouillageInfo?.is_verrouille) {
      toast.error("Impossible de supprimer : les salaires sont verrouillés.");
      return;
    }
    if (salaires.length === 0) {
      toast.warning("Aucun salaire à supprimer.");
      return;
    }

    const contrat = contrats.find((c) => c.id_contrat === params.id_contrat);
    const employeLabel = contrat
      ? `${contrat.prenom_employe} ${contrat.nom_employe}`
      : "cet employé";

    const result = await Swal.fire({
      icon: "warning",
      title: "Supprimer tous les salaires ?",
      html: `Vous allez supprimer <strong>${salaires.length} enregistrement(s)</strong> pour<br/><strong>${employeLabel}</strong> — ${params.mois} ${params.annee}.<br/><br/>Cette action est irréversible.`,
      showCancelButton: true,
      confirmButtonText: "Oui, tout supprimer",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    setDeletingAll(true);
    try {
      const res = await axios.delete("/salaires/periode/", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          id_contrat: params.id_contrat,
          mois_salaire: params.mois,
          annee_salaire: params.annee,
        },
      });
      toast.success(res.data?.message || "Salaires supprimés avec succès");
      await refreshCurrentSearch();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Erreur lors de la suppression";
      toast.error(msg);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleImpressionBulletin = async () => {
    const params = getActiveSearchParams();
    setDownloading(true);
    try {
      const contrat = contrats.find((c) => c.id_contrat === params.id_contrat);
      const nom = contrat?.nom_employe || "Employe";
      const prenom = contrat?.prenom_employe || "Nom";
      const filename = `Bulletin_${nom}_${prenom}_${params.mois}_${params.annee}.pdf`;
      const response = await axios.post("/bulletins/generer/", null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { id_contrat: params.id_contrat, mois: params.mois, annee: params.annee },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      Swal.fire({ icon: "success", title: "Téléchargement terminé", text: `Le bulletin ${filename} a été téléchargé.`, confirmButtonColor: "#3085d6" });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erreur", text: "Impossible de télécharger le bulletin. Vérifiez les données.", confirmButtonColor: "#d33" });
    } finally { setDownloading(false); }
  };

  const handleDuplicationSalaire = () => {
    if (!filters.id_contrat || !filters.annee || !filters.mois) { toast.error("Veuillez d'abord sélectionner l'employé, l'année et le mois source"); return; }
    setDupMonth(""); setDupMonths([]); setDupYear(String(filters.annee || "")); setDupMultiMonths(false); setEnableDupTargets(false); setSelectedTargetEmployeeIds([]); setDupTargets([]); setShowDupModal(true);
  };

  const isSourcePeriode = (annee, mois) =>
    parseInt(annee) === parseInt(filters.annee) && mois === filters.mois;

  // Ajoute la sélection courante (année + mois) au panier de périodes cibles
  const addDupTargets = () => {
    if (!dupYear) { toast.error("Choisissez une année"); return; }
    const months = dupMultiMonths ? dupMonths : (dupMonth ? [dupMonth] : []);
    if (months.length === 0) { toast.error("Sélectionnez au moins un mois"); return; }
    const yr = parseInt(dupYear);
    setDupTargets((prev) => {
      const seen = new Set(prev.map((t) => `${t.annee}-${t.mois}`));
      const additions = months
        .filter((m) => !isSourcePeriode(yr, m))
        .filter((m) => !seen.has(`${yr}-${m}`))
        .map((m) => ({ annee: yr, mois: m }));
      return [...prev, ...additions];
    });
    setDupMonth(""); setDupMonths([]);
  };

  const removeDupTarget = (annee, mois) =>
    setDupTargets((prev) => prev.filter((t) => !(t.annee === parseInt(annee) && t.mois === mois)));

  const clearDupTargets = () => setDupTargets([]);

  const submitDuplication = async () => {
    // Combine le panier + la sélection courante non encore ajoutée, puis dédoublonne
    const current = [];
    if (dupYear) {
      const months = dupMultiMonths ? dupMonths : (dupMonth ? [dupMonth] : []);
      const yr = parseInt(dupYear);
      months.forEach((m) => current.push({ annee: yr, mois: m }));
    }
    const map = new Map();
    [...dupTargets, ...current].forEach((t) => {
      if (isSourcePeriode(t.annee, t.mois)) return; // exclure la période source
      map.set(`${t.annee}-${t.mois}`, { annee: parseInt(t.annee), mois: t.mois });
    });
    const allTargets = [...map.values()].sort(
      (a, b) => a.annee - b.annee || (ORDRE_MOIS[a.mois] || 13) - (ORDRE_MOIS[b.mois] || 13)
    );

    if (allTargets.length === 0) { toast.error("Ajoutez au moins une période cible"); return; }

    setDuplicating(true);
    try {
      let ok = 0; let skipped = 0; let failed = 0;
      for (const t of allTargets) {
        const payload = {
          id_contrat: filters.id_contrat,
          mois_source: filters.mois,
          annee_source: parseInt(filters.annee),
          mois_cible: t.mois,
          annee_cible: t.annee,
        };
        if (enableDupTargets && selectedTargetEmployeeIds.length > 0) {
          payload.id_employes_cibles = selectedTargetEmployeeIds;
        }
        try {
          await axios.post("/salaires/dupliquer", payload, { headers: { Authorization: `Bearer ${token}` } });
          ok += 1;
        } catch (e) {
          if (e?.response?.status === 400) { skipped += 1; } else { failed += 1; }
        }
      }
      toast.success(`✅ Duplication terminée — Succès: ${ok}, Ignorés: ${skipped}, Échecs: ${failed}`);
      setShowDupModal(false);
      refreshCurrentSearch();
    } catch (error) { toast.error(error?.response?.data?.detail || "Erreur lors de la duplication"); }
    finally { setDuplicating(false); }
  };

  const handleVerrouillage = () => {
    const params = getActiveSearchParams();
    if (!params.id_contrat || !params.annee || !params.mois) {
      toast.error("Veuillez sélectionner un employé, une année et un mois");
      return;
    }
    const contrat = contrats.find((c) => c.id_contrat === params.id_contrat);
    if (contrat) { setEmployeInfo({ nom: contrat.nom_employe, prenom: contrat.prenom_employe, matricule: contrat.matricule_employe }); setShowVerrouillageModal(true); }
  };

  const handleVerrouillageSuccess = () => refreshCurrentSearch();

  const handleMiseAJourBulletin = async () => {
    const params = getActiveSearchParams();
    if (!params.id_contrat || !params.annee || !params.mois) {
      toast.error("Veuillez sélectionner un employé, une année et un mois");
      return;
    }
    setUpdatingBulletin(true);
    try {
      await axios.post("/bulletins/generer-en-arriere-plan/", null, {
        params: { id_contrat: params.id_contrat, mois: params.mois, annee: params.annee },
      });
      toast.success("Bulletin mis à jour en arrière-plan");
    }
    catch (e) { toast.error("Erreur lors de la mise à jour du bulletin"); }
    finally { setUpdatingBulletin(false); }
  };

  const handleImpressionBulletinVerrouille = async () => {
    if (!filters.id_contrat || !filters.annee || !filters.mois) { toast.error("Veuillez sélectionner un employé, une année et un mois"); return; }
    setDownloading(true);
    try {
      const response = await axios.post("/bulletins/generer/", null, { params: { id_contrat: filters.id_contrat, mois: filters.mois, annee: filters.annee }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `bulletin_${filters.mois}_${filters.annee}.pdf`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("Bulletin généré avec succès");
    } catch (error) { toast.error("Erreur lors de la génération du bulletin"); }
    finally { setDownloading(false); }
  };

  const isSearchReady = filters.id_contrat && filters.annee && filters.mois;

  const contratSelectionne = contrats.find((c) => c.id_contrat === filters.id_contrat);

  const renderEmployeeHeader = (employe) => {
    if (!employe) return null;
    const periodes = historiquePeriodes?.periodes || [];
    const nbVerrouilles = periodes.filter((p) => p.is_verrouille).length;
    const nom = employe.nom || employe.nom_employe;
    const prenom = employe.prenom || employe.prenom_employe;

    return (
      <div className="rs-employee-header">
        <div className="rs-employee-avatar" aria-hidden="true">
          {getInitiales(nom, prenom)}
        </div>
        <div className="rs-employee-info">
          <h5>{prenom} {nom}</h5>
          <p className="rs-employee-poste">{employe.poste}</p>
          {historiquePeriodes && (
            <div className="rs-employee-meta">
              <span className="rs-meta-badge">
                {historiquePeriodes.nombre_mois} période{historiquePeriodes.nombre_mois !== 1 ? "s" : ""}
              </span>
              {nbVerrouilles > 0 && (
                <span className="rs-meta-badge rs-meta-locked">
                  {nbVerrouilles} verrouillée{nbVerrouilles !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHistoriqueSousNom = () => {
    if (loadingHistorique) {
      return <p className="historique-loading mb-0">Chargement de l&apos;historique…</p>;
    }
    if (!historiquePeriodes) return null;

    const periodes = historiquePeriodes.periodes || [];
    const parAnnee = grouperPeriodesParAnnee(periodes);
    const anneesTriees = Object.keys(parAnnee).sort((a, b) => Number(a) - Number(b));

    return (
      <div className="historique-periodes-wrap">
        <div className="historique-periodes-header">
          <div className="d-flex align-items-center gap-2">
            <Badge bg="primary" className="historique-summary-badge">
              {historiquePeriodes.nombre_mois} mois
            </Badge>
            <span className="historique-subtitle">Historique des salaires perçus</span>
          </div>
          <div className="historique-legend">
            <Badge bg={null} text={null} className="historique-periode-badge historique-badge-open historique-legend-item">
              Ouvert
            </Badge>
            <Badge bg={null} text={null} className="historique-periode-badge historique-badge-locked historique-legend-item">
              <span className="historique-lock"><FaLock /></span> Verrouillé
            </Badge>
          </div>
        </div>

        {periodes.length > 0 ? (
          anneesTriees.map((annee) => (
            <div key={annee} className="historique-year-group">
              <span className="historique-year-label">{annee}</span>
              <div className="historique-badges-row">
                {parAnnee[annee].map((p) => {
                  const isActive =
                    appliedFilters &&
                    String(p.annee) === String(appliedFilters.annee) &&
                    p.mois === appliedFilters.mois;
                  return (
                    <Badge
                      key={`${p.mois}-${p.annee}`}
                      bg={null}
                      text={null}
                      role="button"
                      tabIndex={0}
                      title={`Afficher les salaires de ${p.mois} ${p.annee}`}
                      className={`historique-periode-badge historique-periode-clickable ${
                        p.is_verrouille ? "historique-badge-locked" : "historique-badge-open"
                      } ${isActive ? "historique-badge-active" : ""}`}
                      onClick={() => handleHistoriquePeriodeClick(p)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleHistoriquePeriodeClick(p);
                        }
                      }}
                    >
                      {p.is_verrouille && (
                        <span className="historique-lock"><FaLock /></span>
                      )}
                      <span className="historique-mois">{p.mois}</span>
                      <span className="historique-montant">
                        {Number(p.net || 0).toLocaleString("fr-FR")} GNF
                      </span>
                    </Badge>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <span className="historique-subtitle">Aucune période enregistrée.</span>
        )}
      </div>
    );
  };

  const contratOptions = contrats.map((c) => ({
    value: c.id_contrat,
    label: `${c.nom_employe} ${c.prenom_employe} | ${c.poste}`,
  }));

  return (
    <div className="salaires-tab-unified salaires-recherche">
      <div className="salaires-toolbar">
        <div className="salaires-toolbar-header">
          <h4 className="salaires-toolbar-title">
            <FaSearch /> Recherche de salaires
          </h4>
          <Button
            onClick={handleReset}
            className="btn-rs-refresh"
            size="sm"
            title="Actualiser"
          >
            <FaSyncAlt /> Actualiser
          </Button>
        </div>
        <Form>
          <Row className="g-3 salaires-filters-row align-items-end">
            <Col xs={12} lg={4}>
              <Form.Label><FaUser /> Employé</Form.Label>
              <div className="rs-select-wrap">
                <Select
                  classNamePrefix="rs"
                  options={contratOptions}
                  value={contratOptions.find((c) => c.value === filters.id_contrat) || null}
                  onChange={(selected) =>
                    setFilters({ ...filters, id_contrat: selected ? selected.value : "" })
                  }
                  isClearable
                  placeholder="Sélectionner un employé..."
                />
              </div>
            </Col>
            <Col xs={6} sm={4} lg={2}>
              <Form.Label><FaCalendarAlt /> Année</Form.Label>
              <Form.Select
                value={filters.annee}
                onChange={(e) => setFilters({ ...filters, annee: e.target.value })}
              >
                <option value="">Année</option>
                {annees.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} sm={4} lg={2}>
              <Form.Label><FaCalendarDay /> Mois</Form.Label>
              <Form.Select
                value={filters.mois}
                onChange={(e) => setFilters({ ...filters, mois: e.target.value })}
              >
                <option value="">Mois</option>
                {moisOptions.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} sm={4} lg="auto" className="rs-btn-col">
              <Form.Label className="rs-btn-label-hidden">Action</Form.Label>
              <Button
                onClick={handleSearch}
                className="btn-rs-search w-100"
                disabled={!isSearchReady || loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : <><FaSearch /> Rechercher</>}
              </Button>
            </Col>
            {role === "RH" && (
              <Col xs={6} sm={4} lg="auto" className="rs-btn-col">
                <Form.Label className="rs-btn-label-hidden">Action</Form.Label>
                <Button
                  onClick={handleDuplicationSalaire}
                  className="btn-rs-duplicate w-100"
                  disabled={!isSearchReady || duplicating || !hasSearched}
                  title="Dupliquer salaire"
                >
                  {duplicating ? <Spinner animation="border" size="sm" /> : <><FaCopy /> Dupliquer</>}
                </Button>
              </Col>
            )}
            {role === "RH" && (
              <Col xs={6} sm={4} lg="auto" className="rs-btn-col">
                <Form.Label className="rs-btn-label-hidden">Action</Form.Label>
                <Button
                  onClick={handleVerrouillage}
                  className="btn-rs-lock w-100"
                  disabled={
                    !isSearchReady ||
                    !hasSearched ||
                    verrouillageInfo?.is_verrouille
                  }
                  title={
                    verrouillageInfo?.is_verrouille
                      ? "Cette période est déjà verrouillée"
                      : "Verrouiller les salaires de cette période"
                  }
                >
                  <FaLock /> Verrouiller
                </Button>
              </Col>
            )}
            {role === "RH" && (
              <Col xs={6} sm={4} lg="auto" className="rs-btn-col">
                <Form.Label className="rs-btn-label-hidden">Action</Form.Label>
                <Button
                  onClick={handleImpressionBulletin}
                  className="btn-rs-print w-100"
                  disabled={!isSearchReady || downloading || !hasSearched}
                  title="Imprimer bulletin"
                >
                  {downloading ? <Spinner animation="border" size="sm" /> : <><FaPrint /> Imprimer</>}
                </Button>
              </Col>
            )}
            {role === "RH" && verrouillageInfo?.is_verrouille && (
              <Col xs={12} sm={6} lg="auto" className="rs-btn-col">
                <Form.Label className="rs-btn-label-hidden">Action</Form.Label>
                <Button
                  onClick={handleMiseAJourBulletin}
                  className="btn-rs-update w-100"
                  disabled={!isSearchReady || updatingBulletin}
                  title="Mettre à jour le bulletin (sans modifier le verrouillage)"
                >
                  {updatingBulletin ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <><FaSyncAlt /> Mettre à jour bulletin</>
                  )}
                </Button>
              </Col>
            )}
          </Row>
        </Form>
      </div>

      {!hasSearched && (
        <div className="salaires-empty-hint">
          <strong>Comment commencer</strong>
          <p className="mb-0 mt-2">Sélectionnez un employé, une année et un mois, puis cliquez sur « Rechercher ».</p>
        </div>
      )}

      {loading ? (
        <div className="rs-loading-block">
          <Spinner animation="border" size="lg" />
          <p className="mt-3 mb-0">Chargement des données...</p>
        </div>
      ) : hasSearched && salaires.length === 0 ? (
        <div>
          {contratSelectionne && (
            <div className="salaires-employee-block">
              {renderEmployeeHeader(contratSelectionne)}
              {renderHistoriqueSousNom()}
            </div>
          )}
          <Alert variant="warning" className="text-center mt-3">
            <strong>Aucun résultat trouvé</strong> pour les critères sélectionnés.
          </Alert>
        </div>
      ) : hasSearched ? (
        <div>
          {(calculsData || calculsMensuelsData) && (
            <div className="salaires-employee-block">
              {renderEmployeeHeader(
                calculsData?.employe ||
                  calculsMensuelsData?.employe ||
                  contratSelectionne
              )}
              {renderHistoriqueSousNom()}
              <Tab.Container id="calculs-tabs" defaultActiveKey="mensuels">
                <Nav variant="pills" className="mb-3 rs-calculs-tabs">
                  <Nav.Item>
                    <Nav.Link eventKey="mensuels">
                      <FaCalendarDay className="me-1" /> {appliedFilters?.mois} {appliedFilters?.annee}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="annuels">
                      <FaChartBar className="me-1" /> Année {appliedFilters?.annee}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="mensuels">
                    {loadingCalculsMensuels ? (
                      <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : calculsMensuelsData ? (
                      <div className="rs-kpi-grid">
                        <RsKpiCard label="Salaire Brut" value={calculsMensuelsData.calculs_mensuels.brut} variant="brut" />
                        <RsKpiCard label="Salaire Net" value={calculsMensuelsData.calculs_mensuels.net} variant="net" />
                        <RsKpiCard label="Charge Salariale" value={calculsMensuelsData.calculs_mensuels.charge_salariale} variant="charge-sal" />
                        <RsKpiCard label="Charge Patronale" value={calculsMensuelsData.calculs_mensuels.charge_patronale} variant="charge-pat" />
                      </div>
                    ) : (
                      <Alert variant="warning" className="text-center mb-0">Aucun calcul mensuel disponible.</Alert>
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="annuels">
                    {loadingCalculs ? (
                      <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : calculsData ? (
                      <div>
                        <div className="rs-kpi-grid mb-3">
                          <RsKpiCard label="Salaire Brut Annuel" value={calculsData.totaux_annuels.brut} variant="brut" />
                          <RsKpiCard label="Salaire Net Annuel" value={calculsData.totaux_annuels.net} variant="net" />
                          <RsKpiCard label="Charge Salariale Annuelle" value={calculsData.totaux_annuels.charge_salariale} variant="charge-sal" />
                          <RsKpiCard label="Charge Patronale Annuelle" value={calculsData.totaux_annuels.charge_patronale} variant="charge-pat" />
                        </div>
                        <div className="rs-annual-months">
                          <h6>Mois avec salaire ({calculsData.nombre_mois} mois)</h6>
                          <div className="d-flex flex-wrap gap-1">
                            {calculsData.mois_avec_salaire.map((mois, index) => (
                              <Badge
                                key={index}
                                bg={mois === appliedFilters?.mois ? "primary" : "secondary"}
                                className={`rs-month-chip ${mois === appliedFilters?.mois ? "active" : ""}`}
                              >
                                {mois}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Alert variant="warning" className="text-center mb-0">Aucun calcul annuel disponible.</Alert>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>
          )}

          <div className="salaires-table-card">
            {verrouillageInfo?.is_verrouille && (
              <div className="rs-lock-alert">
                <FaLock />
                <div>
                  <strong>Salaires verrouillés — {appliedFilters?.mois} {appliedFilters?.annee}</strong>
                  <small>
                    Les salaires de cette période sont verrouillés. Aucune modification ni suppression n&apos;est possible. Seule l&apos;impression des bulletins est autorisée.
                  </small>
                </div>
              </div>
            )}
            <div className="table-section-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6>
                Détails des salaires ({salaires.length} élément{salaires.length !== 1 ? "s" : ""})
              </h6>
              <div className="d-flex align-items-center gap-2">
                {verrouillageInfo?.is_verrouille && (
                  <span className="rs-status-pill-header">
                    <FaLock className="me-1" /> Salaires verrouillés
                  </span>
                )}
                {role === "RH" && !verrouillageInfo?.is_verrouille && salaires.length > 0 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleDeleteAllSalaires}
                    disabled={deletingAll}
                    title="Supprimer tous les enregistrements affichés"
                  >
                    {deletingAll ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <><FaTrash /> Supprimer tout ({salaires.length})</>
                    )}
                  </Button>
                )}
              </div>
            </div>
            <div className="table-responsive">
              <Table className="mb-0 rs-table-modern">
                <thead>
                  <tr>
                    <th>CRG</th>
                    <th>Montant Simple</th>
                    <th>Gain</th>
                    <th>Retenue</th>
                    {role === "RH" && (
                      <th>{verrouillageInfo?.is_verrouille ? "Statut" : "Actions"}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {salaires.map((s, i) => {
                    const crg = crgs.find((c) => c.id_crg === s.id_crg);
                    return (
                      <tr key={i}>
                        <td className="col-crg">{crg?.libelle_crg || "N/A"}</td>
                        <td>{s.montant_simple.toLocaleString("fr-FR")} GNF</td>
                        <td className="col-gain">{s.montant_gain_salaire.toLocaleString("fr-FR")} GNF</td>
                        <td className="col-retenue">{s.montant_retenu_salaire.toLocaleString("fr-FR")} GNF</td>
                        {role === "RH" && (
                          <td>
                            {verrouillageInfo?.is_verrouille ? (
                              <span className="rs-status-pill">
                                <FaLock className="me-1" /> Verrouillé
                              </span>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-warning"
                                  onClick={() => openEdit(s)}
                                  className="rs-action-btn me-1"
                                  title="Modifier"
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => openDelete(s)}
                                  className="rs-action-btn"
                                  title="Supprimer"
                                >
                                  <FaTrash />
                                </Button>
                              </>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      ) : null}

      <SalaireDuplicationModal
        show={showDupModal}
        onHide={() => setShowDupModal(false)}
        onSubmit={submitDuplication}
        duplicating={duplicating}
        sourceEmploye={
          contratSelectionne
            ? `${contratSelectionne.prenom_employe} ${contratSelectionne.nom_employe}`
            : ""
        }
        sourceMois={filters.mois}
        sourceAnnee={filters.annee}
        moisOptions={moisOptions}
        annees={annees}
        contrats={contrats}
        dupYear={dupYear}
        setDupYear={setDupYear}
        dupMonth={dupMonth}
        setDupMonth={setDupMonth}
        dupMonths={dupMonths}
        setDupMonths={setDupMonths}
        dupMultiMonths={dupMultiMonths}
        setDupMultiMonths={setDupMultiMonths}
        dupTargets={dupTargets}
        onAddTargets={addDupTargets}
        onRemoveTarget={removeDupTarget}
        onClearTargets={clearDupTargets}
        enableDupTargets={enableDupTargets}
        setEnableDupTargets={setEnableDupTargets}
        selectedTargetEmployeeIds={selectedTargetEmployeeIds}
        setSelectedTargetEmployeeIds={setSelectedTargetEmployeeIds}
      />

      <SalaireEditModal show={showEditModal} handleClose={() => { setShowEditModal(false); refreshCurrentSearch(); }} salaire={selectedSalaire} token={token} contrats={contrats} crgs={crgs} />
      <ConfirmDeleteSalaireModal show={showDeleteModal} handleClose={() => { setShowDeleteModal(false); refreshCurrentSearch(); }} salaire={selectedSalaire} token={token} />
      <VerrouillageSalaireModal
        show={showVerrouillageModal}
        onHide={() => setShowVerrouillageModal(false)}
        employe={employeInfo}
        mois={getActiveSearchParams().mois}
        annee={getActiveSearchParams().annee}
        idContrat={getActiveSearchParams().id_contrat}
        onVerrouillageSuccess={handleVerrouillageSuccess}
      />
    </div>
  );
};

export default RechercheSalaire;
